import React from 'react';
import { Building, MapPin, Trophy, TrendingUp } from 'lucide-react';

interface Company {
  id: string;
  nome: string;
  piva: string;
  citta?: string;
  provincia?: string;
  total_amount: number;
  tender_count: number;
  win_rate?: number;
}

interface CompanyCardProps {
  company: Company;
  onClick?: (company: Company) => void;
}

const CompanyCard: React.FC<CompanyCardProps> = ({ company, onClick }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  return (
    <div 
      className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => onClick?.(company)}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white line-clamp-2 mb-2">
            {company.nome}
          </h3>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            P.IVA: {company.piva}
          </div>
          {(company.citta || company.provincia) && (
            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
              <MapPin className="h-4 w-4 mr-1" />
              {[company.citta, company.provincia].filter(Boolean).join(', ')}
            </div>
          )}
        </div>
        <div className="text-right">
          <Building className="h-8 w-8 text-gray-400 dark:text-gray-600" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
            {formatCurrency(company.total_amount)}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Valore Totale
          </div>
        </div>
        
        <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">
            {company.tender_count}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Bandi Totali
          </div>
        </div>
        
        {company.win_rate !== undefined && (
          <div className="text-center p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
              {formatPercentage(company.win_rate)}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Tasso Successo
            </div>
          </div>
        )}
      </div>

      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {company.win_rate && company.win_rate > 50 && (
              <div className="flex items-center text-sm text-green-600 dark:text-green-400">
                <Trophy className="h-4 w-4 mr-1" />
                Top Performer
              </div>
            )}
            {company.tender_count > 10 && (
              <div className="flex items-center text-sm text-blue-600 dark:text-blue-400">
                <TrendingUp className="h-4 w-4 mr-1" />
                Attiva
              </div>
            )}
          </div>
          <button className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium">
            Dettagli →
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompanyCard;